import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Award, BadgeCheck, HeartHandshake, Eye, Target, MessageCircle, CalendarHeart, Microscope,
} from "lucide-react";
import { SITE, waLink } from "../lib/site";
import WhatsAppCTABlock from "../components/WhatsAppCTABlock";
import WhyChooseSection from "../components/WhyChooseSection";

export default function QuienSoy() {
  return (
    <div data-testid="page-quien-soy">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 -z-10"
          style={{
            background:
              "linear-gradient(160deg, #F8FBFF 0%, #EEF3F9 60%, #D9E2F2 100%)",
          }}
        />
        <div className="absolute top-20 right-10 w-72 h-72 rounded-full bg-brand-bright/10 blur-3xl" />
        <div className="mx-auto max-w-7xl px-6 md:px-12 py-16 md:py-24 grid lg:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7 order-2 lg:order-1"
          >
            <p className="text-xs uppercase tracking-[0.3em] text-brand-medical mb-4">Quién Soy</p>
            <h1 className="font-serif text-5xl md:text-6xl text-brand-deep leading-[1.05] mb-6">
              Medicina con <em className="not-italic text-gradient-blue">empatía</em>,<br />
              experiencia y precisión.
            </h1>
            <p className="text-lg md:text-xl text-brand-deep/75 font-light leading-relaxed max-w-xl mb-5">
              Soy el <strong className="text-brand-deep font-medium">Dr. Jairo Franco</strong>, Médico Especialista en Ginecología
              y Obstetricia con formación en <strong className="text-brand-deep font-medium">Administración Hospitalaria</strong>,
              enfocado en ofrecer una atención médica de alta calidad, segura y
              centrada en la experiencia de cada paciente.
            </p>
            <p className="text-base text-brand-deep/60 font-light leading-relaxed max-w-xl mb-5">
              Acompaño a cada mujer con escucha cercana, criterio clínico actualizado
              y la calma que merece su cuidado en cada etapa: adolescencia, etapa fértil,
              embarazo, posparto y madurez.
            </p>

            {/* Credentials chips */}
            <ul className="flex flex-wrap gap-2 mb-2" data-testid="credentials-chips">
              <li className="inline-flex items-center gap-1.5 rounded-full bg-white hairline px-3.5 py-1.5 text-xs font-medium text-brand-deep">
                <BadgeCheck className="w-3.5 h-3.5 text-brand-medical" strokeWidth={1.6} />
                Especialista en Ginecología y Obstetricia
              </li>
              <li className="inline-flex items-center gap-1.5 rounded-full bg-brand-medical text-white px-3.5 py-1.5 text-xs font-medium">
                <Award className="w-3.5 h-3.5" strokeWidth={1.6} />
                Maestro en Administración Hospitalaria
              </li>
            </ul>

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={SITE.bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="quien-soy-agendar"
                className="inline-flex items-center gap-2 rounded-full bg-brand-medical hover:bg-brand-deep text-white px-7 py-3.5 font-medium tracking-wide transition-all premium-shadow"
              >
                <CalendarHeart className="w-4 h-4" /> Agendar consulta
              </a>
              <a
                href={SITE.bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="quien-soy-whatsapp"
                className="inline-flex items-center gap-2 rounded-full ring-1 ring-brand-medical/40 text-brand-medical hover:bg-white px-7 py-3.5 font-medium tracking-wide transition-all"
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9 }}
            className="lg:col-span-5 relative order-1 lg:order-2"
          >
            <div className="absolute -inset-4 rounded-[3rem] bg-gradient-to-tr from-brand-bright/30 to-brand-silver/30 blur-2xl" />
            <div className="relative rounded-[2.5rem] overflow-hidden hairline premium-shadow bg-white aspect-[4/5]">
              <img
                src={SITE.images.doctorPortrait}
                alt="Dr. Jairo Franco"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-brand-deep/80 to-transparent">
                <p className="font-serif text-white text-2xl leading-tight">Dr. Jairo Franco</p>
                <p className="text-xs uppercase tracking-[0.2em] text-brand-silver mt-1">
                  Ginecólogo · Obstetra
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CREDENCIALES */}
      <section className="mx-auto max-w-7xl px-6 md:px-12 py-20 md:py-28">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            data-testid="cred-profesional"
            className="relative rounded-3xl p-7 bg-white hairline premium-shadow overflow-hidden"
          >
            <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-brand-bright/15 blur-2xl" />
            <BadgeCheck className="w-7 h-7 text-brand-medical mb-4" strokeWidth={1.3} />
            <p className="text-[10px] uppercase tracking-[0.22em] text-brand-medical mb-2">Cédula profesional</p>
            <p className="font-serif text-3xl text-brand-deep tabular-nums">8169189</p>
            <p className="mt-2 text-brand-deep font-medium text-sm">Médico Cirujano</p>
            <p className="mt-1 text-xs text-brand-deep/60 font-light leading-snug">
              Universidad Michoacana de San Nicolás de Hidalgo (UMSNH)
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.07 }}
            data-testid="cred-especialidad"
            className="relative rounded-3xl p-7 bg-gradient-to-br from-white to-brand-cloud hairline premium-shadow overflow-hidden"
          >
            <div className="absolute top-6 right-6 w-24 h-24 rounded-full bg-brand-bright/15 blur-2xl" />
            <Award className="w-7 h-7 text-brand-medical mb-4" strokeWidth={1.3} />
            <p className="text-[10px] uppercase tracking-[0.22em] text-brand-medical mb-2">Cédula de especialidad</p>
            <p className="font-serif text-3xl text-brand-deep tabular-nums">{SITE.cedulaEspecialidad}</p>
            <p className="mt-2 text-brand-deep font-medium text-sm">Ginecología y Obstetricia</p>
            <p className="mt-1 text-xs text-brand-deep/60 font-light leading-snug">
              Universidad Autónoma del Estado de México
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.14 }}
            data-testid="cred-diplomado"
            className="relative rounded-3xl p-7 bg-white hairline premium-shadow overflow-hidden"
          >
            <div className="absolute -bottom-10 -left-10 w-32 h-32 rounded-full bg-brand-bright/15 blur-2xl" />
            <Microscope className="w-7 h-7 text-brand-medical mb-4" strokeWidth={1.3} />
            <p className="text-[10px] uppercase tracking-[0.22em] text-brand-medical mb-2">Diplomado</p>
            <p className="font-serif text-2xl text-brand-deep leading-tight">Ultrasonido</p>
            <p className="mt-2 text-brand-deep font-medium text-sm">Medica Capacitación</p>
            <p className="mt-1 text-xs text-brand-deep/60 font-light leading-snug">
              Aval: Universidad La Salle / Colegio Nacional de Médicos Militares
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.21 }}
            data-testid="cred-maestria"
            className="relative rounded-3xl p-7 bg-brand-deep text-white hairline overflow-hidden"
          >
            <div className="absolute -bottom-20 -right-20 w-60 h-60 rounded-full bg-brand-bright/20 blur-3xl" />
            <BadgeCheck className="w-7 h-7 text-brand-bright mb-4" strokeWidth={1.3} />
            <p className="text-[10px] uppercase tracking-[0.22em] text-brand-silver mb-2">Posgrado</p>
            <p className="font-serif text-2xl leading-tight">Maestría en Administración Hospitalaria</p>
            <p className="mt-3 text-xs text-white/70 font-light leading-snug">
              Calidad, seguridad y experiencia del paciente
            </p>
          </motion.div>
        </div>
      </section>

      {/* VALORES */}
      <section className="mx-auto max-w-7xl px-6 md:px-12 pb-20 md:pb-28">
        <div className="grid lg:grid-cols-12 gap-12 mb-12">
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.25em] text-brand-medical mb-3">Valores médicos</p>
            <h2 className="font-serif text-4xl md:text-5xl text-brand-deep leading-[1.05]">
              Conocimiento <em className="not-italic text-gradient-blue">llevado a la práctica</em>.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 self-end text-brand-deep/70 font-light leading-relaxed text-lg">
            Mis decisiones clínicas se guían por la evidencia, la prudencia y, sobre
            todo, el respeto absoluto a quien tengo enfrente.
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {[
            { i: HeartHandshake, t: "Empatía", d: "Escucha activa sin prisa, comunicación clara y honesta." },
            { i: BadgeCheck, t: "Profesionalismo", d: "Estándares médicos rigurosos y formación continua." },
            { i: Award, t: "Confianza", d: "Un espacio seguro, privado y libre de juicios." },
          ].map((v, i) => (
            <motion.div
              key={v.t}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, delay: i * 0.08 }}
              data-testid={`valor-${i}`}
              className="bg-white rounded-3xl p-8 hairline premium-shadow hover:-translate-y-1 transition-transform duration-500"
            >
              <div className="w-12 h-12 rounded-2xl bg-brand-cloud ring-1 ring-brand-silver/60 flex items-center justify-center mb-5">
                <v.i className="w-5 h-5 text-brand-medical" strokeWidth={1.4} />
              </div>
              <h3 className="font-serif text-2xl text-brand-deep mb-2">{v.t}</h3>
              <p className="text-brand-deep/70 font-light leading-relaxed">{v.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* MISIÓN / VISIÓN */}
      <section className="mx-auto max-w-7xl px-6 md:px-12 pb-28">
        <div className="grid md:grid-cols-2 gap-5">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            data-testid="mision"
            className="rounded-3xl p-10 bg-white hairline premium-shadow"
          >
            <Target className="w-7 h-7 text-brand-medical mb-5" strokeWidth={1.3} />
            <p className="text-xs uppercase tracking-[0.25em] text-brand-medical mb-2">Misión</p>
            <p className="font-serif text-2xl md:text-3xl text-brand-deep leading-snug">
              Brindar atención ginecoobstétrica de alta calidad, humana y basada en
              evidencia, acompañando a cada mujer en cada etapa de su vida.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            data-testid="vision"
            className="rounded-3xl p-10 bg-gradient-to-br from-brand-cloud to-brand-silver hairline"
          >
            <Eye className="w-7 h-7 text-brand-medical mb-5" strokeWidth={1.3} />
            <p className="text-xs uppercase tracking-[0.25em] text-brand-medical mb-2">Visión</p>
            <p className="font-serif text-2xl md:text-3xl text-brand-deep leading-snug">
              Ser un referente en salud femenina, donde la tecnología médica se une
              con el trato humano para transformar la experiencia de la consulta.
            </p>
          </motion.div>
        </div>
      </section>

      {/* WHY CHOOSE - reuse */}
      <WhyChooseSection />

      {/* WhatsApp CTAs */}
      <WhatsAppCTABlock highlight="cta-consulta" />
    </div>
  );
}
